import DefaultButton from '@/components/common/default/DefaultButton';
import DefaultLink from '@/components/common/default/DefaultLink';
import IndexActivitiesSkeleton from '@/components/common/Skeleton/IndexActivitiesSkeleton';
import SkuTrendingBadge from '@/components/sku/SkuTrendingBadge';
import SkuDescription from '@/components/skuDescription';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { IndexActivitiesProps } from '@/types';
import { SkuDTO } from '@/types/api/sku';
import { CommonEnum } from '@/types/translations/CommonEnum';
import { AskMarketValue, BidMarketValue, OrderMarketValue } from '@/types/utilities';
import { getSkuMainImage, SkuMainImage } from '@/utilites/get-sku-main-image';
import { getMarketValue } from '@/utilites/market-value';
import { toDe } from '@/utilites/sku-operations';
import { Transition } from '@headlessui/react';
import clsx from 'clsx';
import Image from 'next/image';
import { Fragment, memo, useCallback, useMemo, useRef, useState } from 'react';
import { BiChevronDown } from 'react-icons/bi';
import LastActivitiesMobileCard from '../TablesHomeMobile/LastActivities/LastActivitiesMobileCard';
import LastActivitiesMobileDialog from '../TablesHomeMobile/LastActivities/LastActivitiesMobileDialog';
import TopSellersMobileDialog from '../TablesHomeMobile/TopSellers/TopSellersMobileDialog';

// Componentes memoizados para evitar re-renderizados innecesarios
const MemoizedImage = memo(
  ({
    src,
    alt,
    width,
    height,
    className,
  }: {
    src: string;
    alt: string;
    width: number;
    height: number;
    className: string;
  }) => <SkuMainImage src={src} alt={alt} width={width} height={height} className={className} />
);

MemoizedImage.displayName = 'MemoizedImage';

const IndexLastActivitiesSkuImage = memo(({ sku }: { sku: SkuDTO }) => (
  <MemoizedImage
    src={getSkuMainImage(sku.mainImageUrl)}
    alt={sku.name || ''}
    width={68}
    height={68}
    className="tw-rounded-xl tw-border tw-border-solid tw-border-custom-gray tw-object-contain tw-p-1"
  />
));

IndexLastActivitiesSkuImage.displayName = 'IndexLastActivitiesSkuImage';

// Optimizamos con memo y limit-clamp para reducir cantidad de texto
const IndexLastAcitivitesSku = memo(({ sku }: { sku: SkuDTO }) => (
  <div className="tw-w-100 tw-flex tw-items-center tw-justify-center tw-gap-3">
    <div className="tw-text-left">
      <p className="tw-m-0 tw-line-clamp-2 tw-p-0 tw-text-left tw-text-base tw-font-semibold tw-text-textBlack max-nine:tw-line-clamp-1 max-nine:tw-text-sm">
        {sku.name}
      </p>
      <p className="tw-m-0 tw-p-0 tw-text-left tw-text-sm tw-font-medium tw-text-slate-gray/50 max-nine:tw-text-xs max-nine:tw-text-slate-gray">
        <SkuDescription
          skuBottleSizeInMl={sku.bottleSizeInMl}
          skuReleaseDate={sku.releaseDate}
          skuVolumeInPercent={sku.volumeInPercent}
        />
      </p>
    </div>
  </div>
));

IndexLastAcitivitesSku.displayName = 'IndexLastAcitivitesSku';

const IndexLastActivitiesPrice = memo(({ sku }: { sku: SkuDTO }) => {
  // Memoizamos el cálculo del precio
  const price = useMemo(() => {
    if (sku?.asks && sku?.asks.length > 0) return toDe(sku.asks[0].value);
    if (sku?.bids && sku?.bids.length > 0) return toDe(sku.bids[0].value);
    if (sku?.orders && sku?.orders.length > 0) return toDe(sku.orders[0].payValue);
    return toDe(0);
  }, [sku]);

  return <p className="tw-mb-0 tw-p-0 tw-text-[17px] tw-font-medium tw-text-textBlack">{price}</p>;
});

IndexLastActivitiesPrice.displayName = 'IndexLastActivitiesPrice';

const IndexLastActivitiesAction = memo(({ sku }: { sku: SkuDTO }) => {
  const { translate: translateCommon } = useTypedTranslation<CommonEnum>('common');
  // Memoizamos valores calculados
  const { actionType, price, bgColorClass } = useMemo(() => {
    let actionType = '';
    let price = toDe(0);
    let bgColorClass = '';

    if (sku?.bids && sku?.bids.length > 0) {
      actionType = translateCommon(CommonEnum['bid']);
      price = toDe(sku.bids[0].value);
      bgColorClass = 'tw-bg-[#1798611A] tw-text-[#0B613C]';
    } else if (sku?.asks && sku?.asks.length > 0) {
      actionType = translateCommon(CommonEnum['ask']);
      price = toDe(sku.asks[0].value);
      bgColorClass = 'tw-bg-[#15253F1A] tw-text-text';
    } else if (sku?.orders && sku?.orders.length > 0) {
      actionType = translateCommon(CommonEnum['order']);
      price = toDe(sku.orders[0].payValue);
      bgColorClass = 'tw-bg-[#C2AD7D33] tw-text-[#857040]';
    }

    return { actionType, price, bgColorClass };
  }, [sku, translateCommon]);

  return (
    <div
      className={clsx(
        'tw-flex tw-w-full tw-items-center tw-justify-center tw-rounded-lg tw-p-2 max-nine:tw-w-full max-nine:tw-px-2.5 nine:tw-max-w-[95px]',
        bgColorClass
      )}
    >
      <p className="tw-m-0 tw-p-0 tw-text-[14px] tw-font-semibold max-nine:tw-text-center max-nine:tw-text-xs-custom">
        {actionType}
        <br />
        <span className="tw-text-base tw-font-semibold nine:tw-hidden">{price}</span>
      </p>
    </div>
  );
});

IndexLastActivitiesAction.displayName = 'IndexLastActivitiesAction';

const IndexTopSellersPrice = memo(({ sku }: { sku: SkuDTO }) => {
  // Memoizamos el cálculo del precio
  const price = useMemo(() => {
    return toDe(
      getMarketValue(
        (sku?.asks ?? []) as unknown as AskMarketValue[],
        (sku?.bids ?? []) as unknown as BidMarketValue[],
        (sku?.orders ?? []) as unknown as OrderMarketValue[],
        sku.retailPrice || 0
      ).value!
    );
  }, [sku]);

  return <p className="tw-text-base tw-font-medium tw-text-textBlack">{price}</p>;
});

IndexTopSellersPrice.displayName = 'IndexTopSellersPrice';

// Memoizamos toda la tabla para evitar re-renders
const IndexLastActivitiesTable = memo(
  ({ lastActivities }: { lastActivities: (SkuDTO & { index: number })[] }) => {
    const { translate: translateCommon } = useTypedTranslation<CommonEnum>('common');

    // Solo renderizamos los primeros 5 elementos para reducir el tamaño del DOM
    const limitedActivities = useMemo(() => lastActivities.slice(0, 6), [lastActivities]);

    return (
      <table className="tw-table tw-border-separate tw-border-spacing-y-2 tw-text-center max-nine:tw-w-full">
        <thead>
          <tr className="tw-relative child:tw-py-2">
            <th className="tw-text-center max-nine:tw-hidden">
              <p className="tw-text-[15.79px] tw-font-normal tw-text-slate-gray">#</p>
            </th>
            <th className="tw-w-[15%] tw-text-center max-nine:tw-hidden">
              <span className="tw-sr-only">{translateCommon(CommonEnum['image'])}</span>
            </th>
            <th className="tw-w-[50%] tw-text-left max-nine:tw-w-[90%]">
              <p className="tw-text-[15.79px] tw-font-normal tw-text-slate-gray max-nine:tw-text-xs max-nine:tw-font-semibold max-nine:tw-uppercase">
                {translateCommon(CommonEnum['name'])}
              </p>
            </th>
            <th className="tw-w-[11%] tw-text-center max-nine:tw-hidden">
              <p className="tw-text-[15.79px] tw-font-normal tw-text-slate-gray">
                {translateCommon(CommonEnum['price'])}
              </p>
            </th>
            <th className="tw-w-[19%] tw-text-center max-nine:tw-w-[10%]">
              <span className="tw-sr-only">{translateCommon(CommonEnum['action'])}</span>
              <div className="tw-flex tw-items-center tw-justify-center">
                <p className="tw-text-[15.79px] tw-font-normal tw-text-slate-gray max-nine:tw-text-xs max-nine:tw-font-semibold max-nine:tw-uppercase">
                  {translateCommon(CommonEnum['action'])}
                </p>
              </div>
            </th>
            <th
              className="tw-absolute tw-bottom-1 tw-left-0 tw-my-0 tw-h-[1px] tw-w-full tw-border-0 tw-border-none tw-bg-[#5F6D7E4D] !tw-py-0 tw-opacity-100 nine:tw-hidden"
              aria-hidden="true"
              tabIndex={-1}
            />
          </tr>
        </thead>
        <tbody>
          {limitedActivities.map((sku, index) => (
            <tr
              key={sku.id}
              className={index === 5 ? 'max-nine:tw-table-row nine:tw-hidden' : 'tw-table-row'}
            >
              <td className="tw-w-[5%] tw-text-center max-nine:tw-hidden">
                <span className="tw-text-base tw-font-semibold tw-text-slate-gray">
                  {sku.index + 1}
                </span>
              </td>
              <td className="tw-w-[15%] tw-text-center max-nine:tw-hidden">
                <DefaultLink href={`/${sku.slug}`} className="tw-flex tw-justify-center">
                  <IndexLastActivitiesSkuImage sku={sku} />
                </DefaultLink>
              </td>
              <td className="tw-w-[50%] tw-text-center">
                <span className="tw-sr-only">Bottle</span>
                <div className="tw-flex tw-h-[68px] tw-w-full tw-items-center tw-justify-center tw-gap-3">
                  <div className="nine:tw-hidden">
                    <DefaultLink href={`/${sku.slug}`}>
                      <IndexLastActivitiesSkuImage sku={sku} />
                    </DefaultLink>
                  </div>
                  <DefaultLink href={`/${sku.slug}`} className="tw-flex tw-w-full">
                    <IndexLastAcitivitesSku sku={sku} />
                  </DefaultLink>
                </div>
              </td>
              <td className="tw-w-[11%] tw-text-center max-nine:tw-hidden">
                <div className="tw-flex tw-h-[68px] tw-items-center tw-justify-center">
                  <IndexLastActivitiesPrice sku={sku} />
                </div>
              </td>
              <td className="tw-w-[19%] tw-whitespace-nowrap tw-text-center">
                <span className="tw-sr-only">Action</span>
                <div className="tw-flex tw-w-full tw-items-center tw-justify-center">
                  <div className="tw-flex tw-h-[68px] tw-w-full tw-items-center tw-justify-center">
                    <IndexLastActivitiesAction sku={sku} />
                  </div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
);

IndexLastActivitiesTable.displayName = 'IndexLastActivitiesTable';

// Memoizamos la tabla de top sellers
const IndexTopSellersTable = memo(
  ({ topSellers }: { topSellers: (SkuDTO & { index: number })[] }) => {
    // Solo renderizamos los primeros 5 elementos
    const limitedSellers = useMemo(() => topSellers.slice(0, 6), [topSellers]);

    const { translate: translateCommon } = useTypedTranslation<CommonEnum>('common');

    return (
      <table className="tw-table tw-border-separate tw-border-spacing-y-2 tw-text-center max-nine:tw-w-full">
        <thead className="tw-overflow-hidden nine:tw-h-[64px]">
          <tr className="tw-relative child:tw-py-2">
            <th className="tw-text-center max-nine:tw-hidden">
              <p className="tw-text-[15.79px] tw-font-normal tw-text-slate-gray">#</p>
            </th>
            <th className="tw-w-[15%] tw-text-center max-nine:tw-hidden">
              <span className="tw-sr-only">{translateCommon(CommonEnum['image'])}</span>
            </th>
            <th className="tw-w-[50%] tw-text-left max-nine:tw-w-[90%]">
              <p className="tw-text-[15.79px] tw-font-normal tw-text-slate-gray max-nine:tw-text-xs max-nine:tw-font-semibold max-nine:tw-uppercase">
                {translateCommon(CommonEnum['name'])}
              </p>
            </th>
            <th className="tw-w-[15%] tw-text-center max-nine:tw-hidden">
              <p className="tw-text-[15.79px] tw-font-normal tw-text-slate-gray">
                {translateCommon(CommonEnum['price'])}
              </p>
            </th>
            <th className="tw-w-[15%] tw-text-center max-nine:tw-w-[10%]">
              <span className="tw-sr-only">{translateCommon(CommonEnum['trending'])}</span>
              <div className="tw-flex tw-items-center tw-justify-center">
                <p className="tw-text-[15.79px] tw-font-normal tw-text-slate-gray max-nine:tw-text-xs max-nine:tw-font-semibold max-nine:tw-uppercase">
                  {translateCommon(CommonEnum['trending'])}
                </p>
              </div>
            </th>
            <th
              className="tw-absolute tw-bottom-1 tw-left-0 tw-my-0 tw-h-[1px] tw-w-full tw-border-0 tw-border-none tw-bg-[#5F6D7E4D] !tw-py-0 tw-opacity-100 nine:tw-hidden"
              aria-hidden="true"
              tabIndex={-1}
            />
          </tr>
        </thead>
        <tbody>
          {limitedSellers.map((sku, index) => (
            <tr
              key={sku.id}
              className={index === 5 ? 'max-nine:tw-table-row nine:tw-hidden' : 'tw-table-row'}
            >
              <td className="tw-w-[5%] tw-text-center max-nine:tw-hidden">
                <span className="tw-text-base tw-font-semibold tw-text-slate-gray">
                  {sku.index + 1}
                </span>
              </td>
              <td className="tw-w-[15%] tw-text-center max-nine:tw-hidden">
                <DefaultLink href={`/${sku.slug}`} className="tw-flex tw-justify-center">
                  <IndexLastActivitiesSkuImage sku={sku} />
                </DefaultLink>
              </td>
              <td className="tw-w-[50%] tw-text-center">
                <span className="tw-sr-only">Bottle</span>
                <div className="tw-flex tw-h-[68px] tw-w-full tw-items-center tw-justify-center tw-gap-3">
                  <div className="nine:tw-hidden">
                    <DefaultLink href={`/${sku.slug}`}>
                      <IndexLastActivitiesSkuImage sku={sku} />
                    </DefaultLink>
                  </div>
                  <DefaultLink href={`/${sku.slug}`} className="tw-flex tw-w-full">
                    <IndexLastAcitivitesSku sku={sku} />
                  </DefaultLink>
                </div>
              </td>
              <td className="tw-w-[15%] tw-text-center max-nine:tw-hidden">
                <div className="tw-flex tw-h-[68px] tw-items-center tw-justify-center">
                  <IndexLastActivitiesPrice sku={sku} />
                </div>
              </td>
              <td className="tw-w-[15%] tw-whitespace-nowrap tw-text-center">
                <span className="tw-sr-only">Trending</span>
                <div className="tw-flex tw-w-full tw-items-center tw-justify-center">
                  <div className="tw-flex tw-h-[68px] tw-w-full tw-items-center tw-justify-center">
                    <SkuTrendingBadge sku={sku} />
                  </div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
);

IndexTopSellersTable.displayName = 'IndexTopSellersTable';

// Función para generar tarjetas de SKU - memoizada
const SkuCard = ({ product, index }: { product: SkuDTO | null; index: number }) => {
  const { mainImageUrl = null, name = null, slug = null } = product || {};

  return (
    <LastActivitiesMobileCard
      key={
        product
          ? `LastActivitiesMobile-${product.slug}-${product.id}-${index}-mobile`
          : `LastActivitiesMobile-${index}-mobile`
      }
      product={product}
      slug={slug}
      productMainImageUrl={mainImageUrl}
      productName={name}
    />
  );
};

SkuCard.displayName = 'SkuCard';

// Componente principal - también memoizado
const IndexActivities = memo(({ lastActivities, topSellers, isLoading }: IndexActivitiesProps) => {
  const [activeActivitiesTab, setActiveActivitiesTab] = useState<'lastActivities' | 'mostSold'>(
    'lastActivities'
  );
  const lastActivitiesButtonRef = useRef<HTMLButtonElement>(null);
  const mostSoldButtonRef = useRef<HTMLButtonElement>(null);
  const { translate: translateCommon } = useTypedTranslation<CommonEnum>('common');
  const [showLastActivitesMobileDialog, setShowLastActivitesMobileDialog] = useState(false);
  const [showTopSellersMobileDialog, setShowTopSellersMobileDialog] = useState(false);

  // Memoizamos el procesamiento de datos
  const processedLastActivities = useMemo(
    () =>
      lastActivities.map((sku, index) => ({
        ...sku,
        index,
      })),
    [lastActivities]
  );

  const processedTopSellers = useMemo(
    () =>
      topSellers.map((sku, index) => ({
        ...sku,
        index,
      })),
    [topSellers]
  );

  // Callback para cambiar tabs
  const handleTabChange = useCallback((tab: 'lastActivities' | 'mostSold') => {
    setActiveActivitiesTab(tab);
  }, []);

  const generateSkuCard = useCallback((product: SkuDTO | null, index: number) => {
    const { mainImageUrl = null, name = null, slug = null } = product || {};
    return (
      <LastActivitiesMobileCard
        key={
          product
            ? `LastActivitiesMobile-${product.slug}-${product.id}-${index}-mobile`
            : `LastActivitiesMobile-${index}-mobile`
        }
        product={product}
        slug={slug}
        productMainImageUrl={mainImageUrl}
        productName={name}
      />
    );
  }, []);

  if (isLoading) {
    return <IndexActivitiesSkeleton />;
  }

  return (
    <div>
      <div className="tw-relative tw-flex tw-items-center tw-gap-9 max-nine:tw-w-full max-nine:tw-justify-between">
        <DefaultButton
          ref={lastActivitiesButtonRef}
          className={clsx(
            activeActivitiesTab === 'lastActivities' ? 'tw-text-text' : 'tw-text-[#5F6D7E99]',
            '!tw-opacity-100 !tw-transition-all !tw-duration-300 !tw-ease-in-out'
          )}
          onClick={() => handleTabChange('lastActivities')}
        >
          <h2 className="tw-pb-2 tw-text-xl tw-font-semibold max-nine:tw-text-lg">
            {translateCommon(CommonEnum['last_activities'])}
          </h2>
        </DefaultButton>
        <DefaultButton
          ref={mostSoldButtonRef}
          className={clsx(
            activeActivitiesTab === 'mostSold' ? 'tw-text-text' : 'tw-text-[#5F6D7E99]',
            '!tw-opacity-100 !tw-transition-all !tw-duration-300 !tw-ease-in-out'
          )}
          onClick={() => handleTabChange('mostSold')}
        >
          <h2 className="tw-pb-2 tw-text-xl tw-font-semibold max-nine:tw-text-lg">
            {translateCommon(CommonEnum['most_sold'])}
          </h2>
        </DefaultButton>
        <hr
          aria-hidden={true}
          className={clsx(
            'tw-absolute -tw-bottom-2 tw-z-10 tw-h-[4px] tw-border-0 tw-border-none tw-bg-text tw-opacity-100',
            'tw-transition-all tw-duration-300 tw-ease-in-out'
          )}
          style={{
            width:
              activeActivitiesTab === 'lastActivities'
                ? `${lastActivitiesButtonRef.current?.offsetWidth}px`
                : `${mostSoldButtonRef.current?.offsetWidth}px`,
            left:
              activeActivitiesTab === 'lastActivities'
                ? `${lastActivitiesButtonRef.current?.offsetLeft}px`
                : `${mostSoldButtonRef.current?.offsetLeft}px`,
          }}
        />
        <hr
          aria-hidden={true}
          className="tw-absolute -tw-bottom-2 tw-h-[2px] tw-w-full tw-border-0 tw-border-none tw-bg-[#F5F5F5] tw-opacity-100 max-nine:tw-hidden"
        />
      </div>

      {/* Content wrapper */}
      <div className="tw-relative tw-pb-[10px]">
        {/* last activities */}
        <Transition
          as={Fragment}
          show={activeActivitiesTab === 'lastActivities'}
          enter="tw-transition-opacity tw-duration-300 tw-ease-in-out"
          enterFrom="tw-opacity-0"
          enterTo="tw-opacity-100"
          leave="tw-transition-opacity tw-duration-300 tw-ease-in-out"
          leaveFrom="tw-opacity-100"
          leaveTo="tw-opacity-0"
        >
          <div className="tw-relative tw-inset-0 nine:tw-flex nine:tw-items-start nine:tw-justify-between nine:tw-gap-8 nine:child:tw-basis-2/4">
            <div className="max-nine:tw-w-full">
              <div className={clsx('max-nine:tw-relative')}>
                <div className="tw-absolute tw-bottom-0 tw-left-0 tw-flex tw-h-[25%] tw-w-full tw-justify-center tw-bg-gradient-to-t tw-from-white/100 tw-via-white/80 tw-to-transparent tw-transition-all tw-duration-300 nine:tw-hidden">
                  <DefaultButton
                    className="tw-relative tw-top-0 tw-mt-[25%] tw-self-start !tw-text-[16px] tw-font-semibold tw-text-main"
                    onClick={() => setShowLastActivitesMobileDialog(true)}
                  >
                    {translateCommon(CommonEnum['see_more'])} <BiChevronDown />
                  </DefaultButton>
                </div>
                <IndexLastActivitiesTable lastActivities={processedLastActivities.slice(0, 6)} />
              </div>
            </div>

            <div className="max-nine:tw-hidden">
              <IndexLastActivitiesTable
                lastActivities={processedLastActivities.slice(5, 10).map(sku => ({
                  ...sku,
                  index: sku.index,
                }))}
              />
            </div>
          </div>
        </Transition>

        {/* most sold */}
        <Transition
          as={Fragment}
          show={activeActivitiesTab === 'mostSold'}
          enter="tw-transition-opacity tw-duration-300 tw-ease-in-out"
          enterFrom="tw-opacity-0"
          enterTo="tw-opacity-100"
          leave="tw-transition-opacity tw-duration-300 tw-ease-in-out"
          leaveFrom="tw-opacity-100"
          leaveTo="tw-opacity-0"
        >
          <div className="tw-relative tw-inset-0 nine:tw-flex nine:tw-items-start nine:tw-justify-between nine:tw-gap-8 nine:child:tw-basis-2/4">
            <div className="max-nine:tw-w-full">
              <div
                className={clsx(
                  'max-nine:tw-relative max-nine:tw-max-h-[580px] max-nine:tw-overflow-hidden'
                )}
              >
                <div className="tw-absolute tw-bottom-0 tw-left-0 tw-flex tw-h-[25%] tw-w-full tw-justify-center tw-bg-gradient-to-t tw-from-white/100 tw-via-white/80 tw-to-transparent tw-transition-all tw-duration-300 nine:tw-hidden">
                  <DefaultButton
                    className="tw-relative tw-top-0 tw-mt-[25%] tw-self-start !tw-text-[16px] tw-font-semibold tw-text-main"
                    onClick={() => setShowTopSellersMobileDialog(true)}
                  >
                    {translateCommon(CommonEnum['see_more'])} <BiChevronDown />
                  </DefaultButton>
                </div>
                <IndexTopSellersTable topSellers={processedTopSellers.slice(0, 6)} />
              </div>
            </div>
            <div className="max-nine:tw-hidden">
              <IndexTopSellersTable
                topSellers={processedTopSellers.slice(5, 10).map(sku => ({
                  ...sku,
                  index: sku.index,
                }))}
              />
            </div>
          </div>
        </Transition>
        {/* This empty div creates space for the absolute positioned content */}
      </div>

      {/* Diálogos móviles */}
      <LastActivitiesMobileDialog
        show={showLastActivitesMobileDialog}
        onClose={() => setShowLastActivitesMobileDialog(false)}
        generateSkuCard={generateSkuCard}
        lastActivities={lastActivities}
      />
      <TopSellersMobileDialog
        show={showTopSellersMobileDialog}
        onClose={() => setShowTopSellersMobileDialog(false)}
        generateSkuCard={generateSkuCard}
        topSellers={topSellers}
      />
    </div>
  );
});

IndexActivities.displayName = 'IndexActivities';

export default IndexActivities;
